import * as Yup from "yup";
import { isValidDocument, isValidPhone } from "./idValidators";

function emptyStringToNull(value, originalValue) {
  if (typeof originalValue === "string" && originalValue === "") {
    return null;
  }
  return value;
}

export const loginSchema = Yup.object({
  email: Yup.string().email("Ingrese un correo válido").required("Requerido"),
  password: Yup.string().required("Requerido"),
});

export const perfilSchema = Yup.object({
  email: Yup.string().email("Ingrese un correo válido"),
  first_name: Yup.string(),
  last_name: Yup.string(),
  document_id: Yup.string().test(
    "valid-document",
    "Documento inválido (DNI/NIE/CIF/Pasaporte)",
    (value) => !value || isValidDocument(value)
  ),
  phone: Yup.string().test(
    "valid-phone",
    "Teléfono inválido (9-15 dígitos)",
    (value) => !value || isValidPhone(value)
  ),
  responsible: Yup.number().typeError("Debe ser un número"),
  prescriptor: Yup.number().typeError("Debe ser un número"),
  bank_account: Yup.string(),
  erp_user_id: Yup.number().typeError("Debe ser un número"),
  delegacion: Yup.string().required("Selecciona una delegación")
});

export const tarifaSchema = (sector_type) =>
  Yup.object({
    type_oferta: Yup.string().required("Requerido"),
    sub_type_offer: Yup.string().required("Requerido"),
    tarifa_comercial: Yup.string().required("Requerido"),
    is_call_tarifa: Yup.boolean().default(false),
    tarifa_oferta: Yup
      .number()
      .typeError('Debe ser un número')
      .required('Se requiere tarifa de acceso'),
    mode_facturacio: Yup.string().required("Requerido"),
    ...(sector_type === 'electrico' && {
      coeficient_k: Yup.number()
        .typeError("Debe ser un número")
        .when("is_call_tarifa", {
          is: true,
          then: (schema) => schema
            .oneOf([0], "Para tarifa CALL el coeficiente K debe ser 0")
            .required("Requerido"),
          otherwise: (schema) => schema
            .integer("Debe ser un número entero")
            .lessThan(36, "Debe ser menor de 36")
            .moreThan(4, "Debe ser mayor de 4")
            .required("Requerido"),
        }),
      coeficient_d: Yup.number().required("Requerido"),
    }),
    ...(sector_type === 'gas' && {
      coeficient_k: Yup.number()
        .typeError("Debe ser un número")
        .when("is_call_tarifa", {
          is: true,
          then: (schema) => schema
            .oneOf([0], "Para tarifa CALL el coeficiente K debe ser 0")
            .required("Requerido"),
          otherwise: (schema) => schema.required("Requerido"),
        }),
    }),
    numero_ctto: Yup.string().when("type_oferta", {
      is: "renovacion",
      then: () => Yup.string().required("Requerido"),
    }),
    showDatePicker: Yup.string().required("Requerido"),
    fecha_estimada_inicio_ctto: Yup.string()
      .nullable()
      .when("showDatePicker", {
        is: "true",
        then: () => Yup.string().required("Requerido"),
      }),
  });

export const buildPartnerSchema = ({ isInternalAgent = false } = {}) => Yup.object({
  cliente_tipo_persona: Yup.string().required(
    "El tipo de persona es requerido"
  ),
  cliente_nombre: Yup.string().when("cliente_tipo_persona", {
    is: "fisica",
    then: () => Yup.string().required("Requerido"),
  }),
  cliente_pais: Yup.number().required("La nacionalidad es requerida"),
  cliente_apellidos: Yup.string().required("Requerido"),
  cliente_vat: Yup.string()
    .required("Requerido")
    .test(
      "valid-cliente-vat",
      "Documento inválido (DNI/NIE/CIF/Pasaporte)",
      (value) => isValidDocument(value)
    ),
  cliente_telefono_1: Yup.string()
    .required("Requerido")
    .test(
      "valid-cliente-phone-1",
      "Teléfono inválido (9-15 dígitos)",
      (value) => isValidPhone(value)
    ),
  cliente_telefono_2: Yup.string()
    .required("Requerido")
    .test(
      "valid-cliente-phone-2",
      "Teléfono inválido (9-15 dígitos)",
      (value) => isValidPhone(value)
    ),
  cliente_email_1: Yup.string()
    .transform((value) => (typeof value === "string" ? value.trim() : value))
    .when(["comPostal", "notificacionPostal"], {
      is: (comPostal, notificacionPostal) => !(comPostal && notificacionPostal),
      then: () => Yup.string().email("Ingrese un correo válido").required("Requerido"),
      otherwise: () => Yup.string().email("Ingrese un correo válido").nullable(),
    }),

  cups_nv: Yup.string()
    .required("Campo Calle es obligatorio")
    .max(100, "La calle no puede exceder los 100 caracteres"),
  cups_pnp: Yup.string().required("Requerido"),
  cups_dp: Yup.string().required("Requerido"),
  cups_id_pais: Yup.number().required("Requerido"),
  cups_id_provincia: Yup.number().required("Requerido"),
  cups_id_municipi: Yup.number().required("Requerido"),
  cups_id_poblacio: Yup.number()
    .typeError("Debe ser un número")
    .transform(emptyStringToNull)
    .nullable(),
  cups_tv: Yup.number().required("Requerido"),
  comPostal: Yup.boolean(),
  notificacionPostal: Yup.boolean(),
}).required();

export const partnerSchema = buildPartnerSchema();

export const pagadorSchema = Yup.object({
  partnerSepa: Yup.boolean(),
  iban: Yup.string().required("Requerido"),
  cliente_tipo_persona: Yup.string().required(
    "El tipo de persona es requerido"
  ),
  cliente_nombre: Yup.string().when("partnerSepa", {
    is: false,
    then: () => Yup.string().required("Requerido"),
  }),
  cliente_apellidos: Yup.string().when("partnerSepa", {
    is: false,
    then: () => Yup.string().required("Requerido"),
  }),
  cliente_vat: Yup.string().when("partnerSepa", {
    is: false,
    then: () =>
      Yup.string()
        .required("Requerido")
        .test(
          "valid-pagador-vat",
          "Documento inválido (DNI/NIE/CIF/Pasaporte)",
          (value) => isValidDocument(value)
        ),
  }),
  cliente_telefono_1: Yup.string().when("partnerSepa", {
    is: false,
    then: () =>
      Yup.string()
        .required("Requerido")
        .test(
          "valid-pagador-phone-1",
          "Teléfono inválido (9-15 dígitos)",
          (value) => isValidPhone(value)
        ),
  }),
  cliente_telefono_2: Yup.string().when("partnerSepa", {
    is: false,
    then: () =>
      Yup.string()
        .required("Requerido")
        .test(
          "valid-pagador-phone-2",
          "Teléfono inválido (9-15 dígitos)",
          (value) => isValidPhone(value)
        ),
  }),
  cliente_email_1: Yup.string()
    .email("Ingrese un correo válido")
    .when("partnerSepa", {
      is: false,
      then: () => Yup.string().required("Requerido"),
    }),
  cliente_nv: Yup.string().when("partnerSepa", {
    is: false,
    then: () => Yup.string().required("Requerido"),
  }),
  cliente_pnp: Yup.string().when("partnerSepa", {
    is: false,
    then: () => Yup.string().required("Requerido"),
  }),
  cliente_dp: Yup.string().when("partnerSepa", {
    is: false,
    then: () => Yup.string().required("Requerido"),
  }),
  cliente_id_pais: Yup.number()
    .typeError("Debe ser un número")
    .when("partnerSepa", {
      is: false,
      then: () =>
        Yup.number().typeError("Debe ser un número").required("Requerido"),
    }),
  cliente_id_provincia: Yup.number()
    .typeError("Debe ser un número")
    .when("partnerSepa", {
      is: false,
      then: () =>
        Yup.number().typeError("Debe ser un número").required("Requerido"),
    }),
  cliente_id_municipi: Yup.number()
    .typeError("Debe ser un número")
    .when("partnerSepa", {
      is: false,
      then: () =>
        Yup.number().typeError("Debe ser un número").required("Requerido"),
    }),
  cliente_id_poblacio: Yup.number()
    .typeError("Debe ser un número")
    .transform(emptyStringToNull)
    .nullable(),
  cliente_tv: Yup.number()
    .typeError("Debe ser un número")
    .when("partnerSepa", {
      is: false,
      then: () =>
        Yup.number().typeError("Debe ser un número").required("Requerido"),
    }),
}).required();

export const cupsSchema = (sector_type) =>
  Yup.object({
    autoconsumo_id: Yup.string().test(
      "autoconsumo_required",
      "Requerido",
      (value) =>
        sector_type !== "electrico" || (sector_type === "electrico" && value)
    ),
    // caudal_diario: Yup.string().test(
    //   "caudal_required",
    //   "Requerido",
    //   (value) => sector_type === "electrico" || (sector_type !== "electrico" && value)
    // ),
    // pressio_cups: Yup.string().test(
    //   "pressio_required",
    //   "Requerido",
    //   (value) => sector_type === "electrico" || (sector_type !== "electrico" && value)
    // ),
    // En tu archivo de esquemas de validación (validationSchemas.js)
    cnae: Yup.string().required('El CNAE es requerido'),
    cnae_desc: Yup.string().test("empty-cnae", "Requerido", function (value) {
      const match = "No existe el CNAE";
      return value !== match;
    }),
    //cups_ref_catastral: Yup.string().required("Requerido"),
    /*consumo_anual_previsto: Yup.number()
      .integer("Debe ser un número entero")
      .positive("Debe ser mayor a 0")
      .typeError("Debe ser un número entero")
      .required("Requerido"),*/
    cups_distribuidora_id: Yup.mixed().test(
      "distribuidora_required",
      "Requerido",
      function (value) {
        const distName = String(this.parent?.cups_distribuidora_name || "").trim();
        const hasId = value !== null && value !== undefined && String(value).trim() !== "";

        if (hasId) {
          return !Number.isNaN(Number(value));
        }

        return distName.length > 0;
      }
    ),
    cups_distribuidora_name: Yup.string(),
    cups_name: Yup.string()
      .nullable()
      //.transform((o, c) => (o === "" ? null : c))
      .min(18, "Cantidad de caracteres inválida")
      .max(22, "Cantidad de caracteres inválida"),
    cups_pnp: Yup.string().when("mismaDireccion", {
      is: false,
      then: () => Yup.string().required("Requerido"),
    }),
    cups_id_pais: Yup.number().default(67), // País siempre será España

    cups_id_provincia: Yup.number()
      .required('La provincia es requerida')
      .typeError('Selecciona una provincia válida'),

    cups_id_municipi: Yup.number()
      .required('El municipio es requerido')
      .typeError('Selecciona un municipio válido'),

    cups_id_poblacio: Yup.number()
      .required('La población es requerida')
      .typeError('Selecciona una población válida'),
    cups_tv: Yup.number()
      .typeError("Debe ser un número")
      .required("El tipo de vía es requerido"),
    cups_nv: Yup.string()
      .required("La calle es requerida")
      .max(100, "La calle no puede exceder los 100 caracteres"),

    cups_dp: Yup.string()
      .required("El código postal es requerido")
      .matches(/^\d{5}$/, "El código postal debe tener 5 dígitos"),
    potencias: Yup.object().when('sector_type', {
      is: 'electrico',
      then: () => Yup.object().test(
        'potencias-valid',
        'Debe ingresar valores válidos para todas las potencias',
        function (value) {
          if (!value) return true;
          return Object.values(value).every(
            val => val === '' || /^[0-9]+,*([0-9])*$/.test(val)
          );
        }
      )
    }),
  }).required();
